import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import notificationApi, { subscribeToPush, unsubscribeFromPush } from '../services/notificationApi';
import profileApi from '../pages/Profile/profileApi';

const PushNotificationContext = createContext(null);

const STORAGE_KEY = 'cm_unread_count';
const PWA_PROMPT_SESSION_KEY = 'cm_pwa_push_prompted';

export const PushNotificationProvider = ({ children, isPWA = false }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  });
  const bcRef = useRef(null);

  // ── Load unread count from server (called on page load only) ──────────────
  const refreshUnreadCount = useCallback(async () => {
    if (!user) { setUnreadCount(0); return; }
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count);
      localStorage.setItem(STORAGE_KEY, String(count));
    } catch {
      // non-blocking
    }
  }, [user]);

  // On mount / user change, fetch from server once (page load semantics)
  useEffect(() => {
    if (user) {
      refreshUnreadCount();
    } else {
      setUnreadCount(0);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user, refreshUnreadCount]);

  // ── BroadcastChannel: increment badge when SW receives a push ─────────────
  useEffect(() => {
    if (!user || typeof BroadcastChannel === 'undefined') return;

    const bc = new BroadcastChannel('cm_push');
    bcRef.current = bc;
    bc.onmessage = (event) => {
      if (event.data?.type === 'push_received') {
        setUnreadCount((prev) => {
          const next = prev + 1;
          localStorage.setItem(STORAGE_KEY, String(next));
          return next;
        });
      }
    };
    return () => { bc.close(); bcRef.current = null; };
  }, [user]);

  // ── Public helpers ─────────────────────────────────────────────────────────
  const decrementUnread = useCallback((by = 1) => {
    setUnreadCount((prev) => {
      const next = Math.max(0, prev - by);
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
    localStorage.setItem(STORAGE_KEY, '0');
  }, []);

  // ── Push subscription helpers ──────────────────────────────────────────────
  const enablePush = useCallback(async () => {
    if (!('Notification' in window)) return { ok: false, error: 'Notifications not supported' };
    if (Notification.permission === 'denied') return { ok: false, error: 'Permission denied' };

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return { ok: false, error: 'Permission not granted' };

      const vapidKey = await notificationApi.getVapidPublicKey();
      const sub = await subscribeToPush(vapidKey);
      if (!sub) return { ok: false, error: 'Subscription failed' };

      await notificationApi.registerSubscription(sub.toJSON());
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.message || 'Unknown error' };
    }
  }, []);

  const disablePush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await notificationApi.unregisterSubscription(sub.endpoint);
        await unsubscribeFromPush();
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.message || 'Unknown error' };
    }
  }, []);

  const isPushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  const getPushPermission = () => {
    if (!isPushSupported) return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
  };

  // ── PWA auto-prompt: ask for push permission once per session ─────────────
  useEffect(() => {
    if (!user || !isPWA || !isPushSupported) return;
    if (Notification.permission !== 'default') return;
    if (sessionStorage.getItem(PWA_PROMPT_SESSION_KEY)) return;

    sessionStorage.setItem(PWA_PROMPT_SESSION_KEY, '1');

    (async () => {
      const result = await enablePush();
      if (result.ok) {
        try {
          await profileApi.grantPrivacyConsents(['notifications'], null, 'pwa_auto');
        } catch {
          // non-blocking
        }
      }
    })();
  }, [user, isPWA, isPushSupported, enablePush]);

  const isCurrentlySubscribed = useCallback(async () => {
    if (!isPushSupported) return false;
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      if (!regs.length) return false;
      const active = regs.find((r) => r.active || r.installing || r.waiting);
      if (!active) return false;
      const sub = await active.pushManager.getSubscription();
      return sub !== null;
    } catch {
      return false;
    }
  }, [isPushSupported]);

  return (
    <PushNotificationContext.Provider value={{
      unreadCount,
      refreshUnreadCount,
      decrementUnread,
      clearUnread,
      enablePush,
      disablePush,
      isPushSupported,
      getPushPermission,
      isCurrentlySubscribed,
    }}>
      {children}
    </PushNotificationContext.Provider>
  );
};

export const usePushNotifications = () => useContext(PushNotificationContext);

export default PushNotificationContext;
